/**
 * The small mark beside an item field that crosses to another system: that system's logo and
 * which way the field moves. Hover (or focus) reads the full sentence. A field with no mark is
 * kept in Priorities.ai only (canon 01 › Field Source, Sync Direction and Conflict Rule).
 */
import { ConnectorLogo } from '@/components/ui/ConnectorLogo';
import { sourceWords, type FieldSource } from '@/lib/itemFieldSources';

export function FieldSourceMark({ source }: { source: FieldSource }) {
  const body = (
    <>
      <ConnectorLogo type={source.connectorType} size={13} />
      <span>{sourceWords(source)}</span>
    </>
  );
  const cls =
    'inline-flex items-center gap-1 align-middle font-sans text-[12px] font-normal leading-none text-[var(--text-dim)]';
  return source.externalUrl ? (
    <a href={source.externalUrl} target="_blank" rel="noreferrer" className={`${cls} hover:underline`} title={source.sentence} data-testid="field-source-mark">
      {body}
    </a>
  ) : (
    <span className={cls} title={source.sentence} tabIndex={0} data-testid="field-source-mark">
      {body}
    </span>
  );
}

export function FieldSourceMarks({ sources, className = '' }: { sources?: FieldSource[]; className?: string }) {
  if (!sources?.length) return null;
  return (
    <span className={`inline-flex flex-wrap items-center gap-2 ${className}`.trim()}>
      {sources.map((s) => (
        <FieldSourceMark key={`${s.connectorType}:${s.field}`} source={s} />
      ))}
    </span>
  );
}

/** In place of an edit control, for a field kept in another system that takes no changes back. */
export function ChangeItThere({ source }: { source: FieldSource }) {
  const text = `Change it in ${source.systemName}`;
  return source.externalUrl ? (
    <a href={source.externalUrl} target="_blank" rel="noreferrer" className="text-[13.5px] underline text-[var(--text-sec)]">
      {text}
    </a>
  ) : (
    <span className="text-[13.5px] text-[var(--text-sec)]">{text}</span>
  );
}
